/* ===================================================================
   Google review-link / Maps-URL normalizer (2026-07-09).

   THE unblocker for service-area / hidden-address businesses (SAB): a large
   slice of the trades ICP has a Google Business Profile that Google's Places
   TEXT SEARCH cannot find (verified: Steve's "Own The Box Marketing LLC" returns
   ZERO_RESULTS by name AND by phone). Name-search tuning can never connect them.
   Instead, the owner pastes their OWN Google review link / Maps share URL and we
   use it directly as the review destination.

   This module parses whatever the owner pastes into a normalized shape:
     - placeId   : a Google place_id, when one is extractable from the input
                   (raw id, ?placeid=, place_id:, or a Maps ftid 0x..:0x..).
                   A place_id lets the backend do a Places DETAILS lookup, which
                   WORKS even for listings that text-search can't find, and lets
                   us import/monitor reviews.
     - reviewUrl : the URL to send customers to in order to leave a review. For a
                   g.page/short link or a Maps URL with no extractable id, THIS
                   is the whole point -- it works without any place_id at all.
     - kind      : which shape we recognized (for UI copy + backend routing).
     - valid     : true when the input is a plausible Google review/Maps link or
                   a place_id (so the UI can gate the submit button).

   Pure + dependency-free. The backend does the authoritative resolution
   (follow shortlink redirects, Places Details, validate) -- this is the
   best-effort client pre-parse so the common cases resolve instantly and the
   submit button can validate the paste.
   =================================================================== */

export type ReviewLinkKind =
  | 'place_id' // a bare place_id
  | 'writereview' // search.google.com/local/writereview?placeid=...
  | 'maps_place_id' // a maps/other URL carrying place_id= / place_id:
  | 'gpage' // g.page/... short review link (no id, URL is the destination)
  | 'maps' // a maps.google / maps.app.goo.gl / goo.gl/maps URL (server resolves)
  | 'unknown';

export interface ParsedReviewLink {
  raw: string;
  valid: boolean;
  kind: ReviewLinkKind;
  /** Extracted Google place_id, when present in the input. */
  placeId?: string;
  /** The review destination URL to store/send when there is no place_id (or as
      the canonical writereview URL once a place_id is known). */
  reviewUrl?: string;
}

/* A Google place_id is an opaque token: the "ChIJ..."/"GhIJ..." Base64-ish ids.
   The hex "ftid" pair 0x<hex>:0x<hex> from Maps URLs is deliberately NOT here --
   Places Details rejects it (400 INVALID_ARGUMENT) and a writereview URL built
   from it lands on a Google error page. An ftid is surfaced, never canonicalized. */
const BARE_PLACE_ID = /^(ChIJ[A-Za-z0-9_-]{10,}|Gh[A-Za-z0-9_-]{10,})$/;
const PLACE_ID_PARAM = /[?&#](?:placeid|place_id)=([A-Za-z0-9_-]{10,})/i;
const PLACE_ID_COLON = /place_id:([A-Za-z0-9_-]{10,})/i;
/* Maps "ftid" feature id (…!1s0x…:0x… or data=…0x..:0x..). */
const MAPS_FTID = /!1s(0x[0-9a-fA-F]+:0x[0-9a-fA-F]+)/;

function canonicalWriteReview(placeId: string): string {
  return `https://search.google.com/local/writereview?placeid=${encodeURIComponent(placeId)}`;
}

export function parseReviewLink(input: string): ParsedReviewLink {
  const raw = (input ?? '').trim();
  if (!raw) return { raw, valid: false, kind: 'unknown' };

  // 1) A bare place_id (no URL). Most reliable -> canonical writereview URL.
  if (BARE_PLACE_ID.test(raw)) {
    return {
      raw,
      valid: true,
      kind: 'place_id',
      placeId: raw,
      reviewUrl: canonicalWriteReview(raw),
    };
  }

  // 2) place_id carried in a URL/query (?placeid=, place_id:, place_id=).
  const paramMatch = raw.match(PLACE_ID_PARAM) || raw.match(PLACE_ID_COLON);
  if (paramMatch) {
    const pid = paramMatch[1];
    const isWriteReview = /writereview/i.test(raw);
    return {
      raw,
      valid: true,
      kind: isWriteReview ? 'writereview' : 'maps_place_id',
      placeId: pid,
      reviewUrl: canonicalWriteReview(pid),
    };
  }

  // 3) A Maps URL with an ftid (…!1s0x..:0x..). The ftid is not a place_id but
  //    the backend can resolve it; pass the URL through + surface the ftid.
  const ftid = raw.match(MAPS_FTID);
  const isUrl = /^https?:\/\//i.test(raw);

  // 4) g.page short review link -> the URL IS the review destination (SAB case).
  //    Normalize a bare g.page/<code> to its /review form when it's clearly the
  //    review shortlink; leave an explicit /review as-is.
  if (/(^|\/\/)g\.page\//i.test(raw)) {
    let url = raw;
    if (/g\.page\/r\//i.test(raw) && !/\/review\/?($|\?)/i.test(raw)) {
      url = raw.replace(/\/?$/, '/review');
    }
    return { raw, valid: true, kind: 'gpage', reviewUrl: url };
  }

  // 5) Any other Google Maps / short URL: keep the URL as the destination and
  //    let the backend follow redirects + Places-resolve best-effort.
  if (
    isUrl &&
    /(google\.[a-z.]+\/maps|maps\.google\.|maps\.app\.goo\.gl|goo\.gl\/maps|search\.google\.com\/local)/i.test(raw)
  ) {
    return {
      raw,
      valid: true,
      kind: 'maps',
      reviewUrl: raw,
      ...(ftid ? { placeId: undefined } : {}),
    };
  }

  // Not recognized as a Google review/Maps link or a place_id.
  return { raw, valid: false, kind: 'unknown', ...(isUrl ? { reviewUrl: raw } : {}) };
}
